import { InventoryTransactionType, IssueTaskCategory, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, DatePicker, Input, InputNumber, Popconfirm, Popover, Space, message } from 'antd';
import { formatDate } from 'date-fns';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { issueTaskCategoryKr } from 'src/constants/issue.constants';

import { loadGridState } from 'src/utils/ag-grid-util';
import { formatDate as formatDateUtil } from 'src/utils/date-util';
import { callInventoryIssueTransaction } from 'src/utils/firebase/firebase-callable';
import { updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import { createTaskCategoryCellRenderer } from 'src/utils/issue/issue-ag-grid-renderers';
import { registeredAtValueFormatter, taskCategoryColorMap } from 'src/utils/issue/issue-ag-grid-util';
import { issueTaskStatusBadgeMap } from 'src/utils/issue/issue-util';

import { useInventory } from 'src/redux/hooks/useInventory';

import Loading from 'src/components/Loading/Loading';

import IssuePanelModal, { IssuePanelModalData } from '../IssuePanelModal';
import { InventoryIssueRow, inventoryStatusBadgeMap } from './types';

interface IssueInventoryManagementTableProps {
  rowData: InventoryIssueRow[];
  isLoading: boolean;
  onGridRefChange?: (gridRef: AgGridReact<InventoryIssueRow> | null) => void;
}

export const ISSUE_INVENTORY_MANAGEMENT_TABLE_ID = 'issueInventoryManagementTable';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

/**
 * Task 카테고리 포매터
 */
const taskCategoryValueFormatter = (params: ValueFormatterParams<InventoryIssueRow, IssueTaskCategory>) => {
  if (!params.value) return '-';
  return issueTaskCategoryKr[params.value] || params.value;
};

/**
 * Task 상태 포매터
 */
const taskStatusValueFormatter = (params: ValueFormatterParams<InventoryIssueRow, IssueTaskStatus>) => {
  if (!params.value) return null;
  if (!issueTaskStatusBadgeMap[params.value]) return String(params.value);
  return issueTaskStatusBadgeMap[params.value].text;
};

/**
 * 재고 처리 상태 렌더러
 */
const InventoryStatusCellRenderer = (params: ICellRendererParams<InventoryIssueRow>) => {
  const status = params.data?.inventoryStatus;
  if (!status) return '-';
  const badgeInfo = inventoryStatusBadgeMap[status];
  if (!badgeInfo) return status;

  const antBadgeStatusMap: Record<string, 'success' | 'processing' | 'default' | 'error' | 'warning'> = {
    success: 'success',
    warning: 'warning',
    error: 'error',
    default: 'default',
  };

  return <Badge status={antBadgeStatusMap[badgeInfo.color] || 'default'} text={badgeInfo.text} />;
};

/**
 * 금액 포맷팅
 */
const formatAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(amount);
};

const IssueInventoryManagementTable: FC<IssueInventoryManagementTableProps> = ({
  rowData,
  isLoading,
  onGridRefChange,
}) => {
  const [gridRef, setGridRef] = useState<AgGridReact<InventoryIssueRow> | null>(null);

  // gridRef 변경 시 부모에게 전달
  const handleGridRef = useCallback(
    (ref: AgGridReact<InventoryIssueRow> | null) => {
      setGridRef(ref);
      onGridRefChange?.(ref);
    },
    [onGridRefChange]
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IssuePanelModalData>(null);
  const [loadingRowId, setLoadingRowId] = useState<string | null>(null);
  const { onSetSelectedInventory } = useInventory();

  const handleOpenDetail = useCallback((issueCaseId: string) => {
    setModalData({ standaloneIssueId: issueCaseId });
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  /**
   * 카테고리 셀 렌더러 (클릭 시 상세 열기)
   */
  const CategoryCellRenderer = useMemo(
    () => createTaskCategoryCellRenderer<InventoryIssueRow>({ showColor: true, onOpenDetail: handleOpenDetail }),
    [handleOpenDetail]
  );

  /**
   * 입고 처리 핸들러
   */
  const handleInbound = useCallback(async (row: InventoryIssueRow, reason: string, quantity: number) => {
    if (!reason || reason.trim() === '') {
      message.error('입고 사유를 입력해주세요.');
      return;
    }

    if (quantity <= 0) {
      message.error('입고 수량은 0보다 커야 합니다.');
      return;
    }

    if (quantity > row.quantity) {
      message.error(`입고 수량은 최대 ${row.quantity}까지 가능합니다.`);
      return;
    }

    // _originalTask에서 product 정보 가져오기
    const product = row._originalTask.products?.find((p) => p.productId === row.productId);
    if (!product) {
      message.error('상품 정보를 찾을 수 없습니다.');
      return;
    }

    setLoadingRowId(row.rowId);

    try {
      const issueDate = formatDateUtil(new Date(), 'yyyy-MM-dd');

      await callInventoryIssueTransaction({
        body: {
          issueDate,
          inventoryId: product.inventoryId,
          inventoryUnit: product.inventoryUnit,
          transactionType: InventoryTransactionType.INBOUND,
          requestValue: quantity,
          reason,
          byIssueTask: {
            issueTaskId: row.taskId,
            productId: row.productId,
          },
        },
      });

      message.success('입고 처리가 완료되었습니다.');
    } catch (error) {
      console.error('재고 입고 처리 실패:', error);
      message.error('입고 처리에 실패했습니다.');
    }

    setLoadingRowId(null);
  }, []);

  /**
   * 폐기(건너뛰기) 처리 핸들러
   */
  const handleSkip = useCallback(async (row: InventoryIssueRow) => {
    setLoadingRowId(row.rowId);

    try {
      await updateIssueTask(row.taskId, {
        [`inventoryIssueInfo.products.${row.productId}`]: {
          status: 'skipped',
          inventoryIssueId: null,
          processedAt: formatDateUtil(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
        },
      });
      message.success('폐기 처리가 완료되었습니다.');
    } catch (error) {
      console.error('폐기 처리 실패:', error);
      message.error('폐기 처리에 실패했습니다.');
    }

    setLoadingRowId(null);
  }, []);

  /**
   * Task 완료 처리 핸들러
   */
  const handleCompleteTask = useCallback(async (row: InventoryIssueRow) => {
    setLoadingRowId(row.rowId);

    try {
      await updateIssueTask(row.taskId, {
        status: IssueTaskStatus.COMPLETED,
        finishedAt: formatDateUtil(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
      });
      message.success('업무가 완료 처리되었습니다.');
    } catch (error) {
      console.error('업무 완료 처리 실패:', error);
      message.error('업무 완료 처리에 실패했습니다.');
    }

    setLoadingRowId(null);
  }, []);

  /**
   * targetDate 업데이트 핸들러
   */
  const handleUpdateTargetDate = useCallback(async (row: InventoryIssueRow, date: string | null) => {
    setLoadingRowId(row.rowId);

    try {
      await updateIssueTask(row.taskId, {
        targetDate: date,
      });
      message.success('적용 날짜가 변경되었습니다.');
    } catch (error) {
      console.error('적용 날짜 변경 실패:', error);
      message.error('적용 날짜 변경에 실패했습니다.');
    }

    setLoadingRowId(null);
  }, []);

  /**
   * targetDate 셀 렌더러 (IN_PROGRESS일 때 DatePicker로 편집 가능)
   */
  const TargetDateCellRenderer = useCallback(
    (params: ICellRendererParams<InventoryIssueRow>) => {
      const row = params.data;
      if (!row) return '-';

      const { taskStatus, targetDate } = row;
      const isInProgress = taskStatus === IssueTaskStatus.IN_PROGRESS;
      const isLoading = loadingRowId === row.rowId;

      // IN_PROGRESS가 아닌 경우 - 단순 표시
      if (!isInProgress) {
        return targetDate ? formatDate(new Date(targetDate), 'MM.dd') : '-';
      }

      // IN_PROGRESS인 경우 - DatePicker로 편집 가능
      const minDate = dayjs().subtract(1, 'month');
      const maxDate = dayjs().add(1, 'month');

      return (
        <DatePicker
          value={targetDate ? dayjs(targetDate) : null}
          onChange={(date) => {
            const dateStr = date ? date.format('YYYY-MM-DD') : null;
            handleUpdateTargetDate(row, dateStr);
          }}
          minDate={minDate}
          maxDate={maxDate}
          format='MM.DD'
          size='small'
          placeholder='선택'
          allowClear
          disabled={isLoading}
          style={{ width: 90 }}
        />
      );
    },
    [loadingRowId, handleUpdateTargetDate]
  );

  /**
   * 클릭 가능한 Task 상태 셀 렌더러
   * - IN_PROGRESS 상태이고 targetDate가 있을 때만 완료로 변경 가능
   */
  const ClickableTaskStatusCellRenderer = useCallback(
    (params: ICellRendererParams<InventoryIssueRow>) => {
      const row = params.data;
      if (!row) return null;

      const status = row.taskStatus;
      const badgeInfo = issueTaskStatusBadgeMap[status];
      if (!badgeInfo) return String(status);

      const isCompleted = status === IssueTaskStatus.COMPLETED;
      const isInProgress = status === IssueTaskStatus.IN_PROGRESS;
      const hasTargetDate = !!row.targetDate;
      const isLoading = loadingRowId === row.rowId;

      // 완료된 경우 - 단순 표시
      if (isCompleted) {
        return <Badge status={badgeInfo.status} text={badgeInfo.text} />;
      }

      // IN_PROGRESS가 아닌 경우 - 단순 표시 (클릭 불가)
      if (!isInProgress) {
        return <Badge status={badgeInfo.status} text={badgeInfo.text} />;
      }

      // IN_PROGRESS이지만 targetDate가 없는 경우 - 클릭 시 안내
      if (!hasTargetDate) {
        return (
          <Badge
            status={badgeInfo.status}
            text={
              <span
                style={{ cursor: 'not-allowed', color: '#999' }}
                title='적용 날짜를 먼저 입력해주세요'
                onClick={() => message.warning('적용 날짜를 먼저 입력해주세요.')}>
                {badgeInfo.text}
              </span>
            }
          />
        );
      }

      // IN_PROGRESS이고 targetDate가 있는 경우 - 클릭하면 완료로 변경
      return (
        <Popconfirm
          title='업무 완료 처리'
          description='이 Task를 완료 처리하시겠습니까?'
          onConfirm={() => handleCompleteTask(row)}
          okText='완료'
          cancelText='취소'>
          <Badge
            status={badgeInfo.status}
            text={
              <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                {isLoading ? '처리중...' : badgeInfo.text}
              </span>
            }
          />
        </Popconfirm>
      );
    },
    [loadingRowId, handleCompleteTask]
  );

  /**
   * Task 정보 요약 렌더러 (카테고리 + 클릭 가능한 상태)
   */
  const TaskSummaryCellRenderer = useCallback(
    (params: ICellRendererParams<InventoryIssueRow>) => {
      const row = params.data;
      if (!row) return '-';

      const { category, taskStatus, targetDate, issueCaseId } = row;
      if (!category && !taskStatus) return '-';

      const categoryText = category ? issueTaskCategoryKr[category] || category : '';
      const categoryColor = category ? taskCategoryColorMap[category] : undefined;
      const statusBadgeInfo = taskStatus ? issueTaskStatusBadgeMap[taskStatus] : null;

      const isCompleted = taskStatus === IssueTaskStatus.COMPLETED;
      const isInProgress = taskStatus === IssueTaskStatus.IN_PROGRESS;
      const hasTargetDate = !!targetDate;
      const isLoading = loadingRowId === row.rowId;

      const handleCategoryClick = () => {
        if (issueCaseId) {
          handleOpenDetail(issueCaseId);
        }
      };

      // 상태 뱃지 렌더링 함수
      const renderStatusBadge = () => {
        if (!statusBadgeInfo) return null;

        // 완료된 경우 - 단순 표시
        if (isCompleted) {
          return <Badge status={statusBadgeInfo.status} text={statusBadgeInfo.text} />;
        }

        // IN_PROGRESS가 아닌 경우 - 단순 표시
        if (!isInProgress) {
          return <Badge status={statusBadgeInfo.status} text={statusBadgeInfo.text} />;
        }

        // IN_PROGRESS이지만 targetDate가 없는 경우
        if (!hasTargetDate) {
          return (
            <Badge
              status={statusBadgeInfo.status}
              text={
                <span
                  style={{ cursor: 'not-allowed', color: '#999' }}
                  title='예정일을 먼저 입력해주세요'
                  onClick={() => message.warning('예정일을 먼저 입력해주세요.')}>
                  {statusBadgeInfo.text}
                </span>
              }
            />
          );
        }

        // IN_PROGRESS이고 targetDate가 있는 경우 - 클릭하면 완료로 변경
        return (
          <Popconfirm
            title='업무 완료 처리'
            description='이 Task를 완료 처리하시겠습니까?'
            onConfirm={() => handleCompleteTask(row)}
            okText='완료'
            cancelText='취소'>
            <Badge
              status={statusBadgeInfo.status}
              text={
                <span style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                  {isLoading ? '처리중...' : statusBadgeInfo.text}
                </span>
              }
            />
          </Popconfirm>
        );
      };

      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {categoryText && (
            <span
              style={{
                color: categoryColor,
                fontWeight: 500,
                cursor: issueCaseId ? 'pointer' : 'default',
                textDecoration: issueCaseId ? 'underline' : 'none',
              }}
              onClick={handleCategoryClick}>
              {categoryText}
            </span>
          )}
          {renderStatusBadge()}
        </span>
      );
    },
    [loadingRowId, handleCompleteTask, handleOpenDetail]
  );

  /**
   * 재고 처리 액션 셀 렌더러
   */
  const InventoryActionCellRenderer = useCallback(
    (params: ICellRendererParams<InventoryIssueRow>) => {
      const row = params.data;
      if (!row) return null;

      const { taskStatus, inventoryStatus, quantity, storeName } = row;
      const isTaskCompleted = taskStatus === IssueTaskStatus.COMPLETED;
      const isPending = inventoryStatus === 'pending';
      const isLoading = loadingRowId === row.rowId;

      // Task가 완료되지 않은 경우
      if (!isTaskCompleted) {
        return <span style={{ color: '#999', fontSize: 12 }}>업무 미완료</span>;
      }

      // 이미 처리된 경우
      if (!isPending) {
        return <span style={{ color: '#999', fontSize: 12 }}>처리완료</span>;
      }

      // 입고 처리 Popover 컨텐츠
      const InboundPopoverContent = () => {
        const [reason, setReason] = useState(`${storeName} 회수 상품 입고`);
        const [inboundQuantity, setInboundQuantity] = useState(quantity);

        const handleConfirm = () => {
          handleInbound(row, reason, inboundQuantity);
        };

        return (
          <div style={{ width: 280 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>입고 사유</div>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder='입고 사유를 입력하세요'
                size='small'
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>입고 수량 (최대: {quantity})</div>
              <InputNumber
                value={inboundQuantity}
                onChange={(value) => setInboundQuantity(value || 0)}
                min={1}
                max={quantity}
                size='small'
                style={{ width: '100%' }}
              />
            </div>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button type='primary' size='small' onClick={handleConfirm} loading={isLoading}>
                입고
              </Button>
            </Space>
          </div>
        );
      };

      return (
        <Space size={4}>
          <Popover content={<InboundPopoverContent />} title='입고 처리' trigger='click' placement='left'>
            <Button type='primary' size='small' loading={isLoading}>
              입고
            </Button>
          </Popover>
          <Popconfirm
            title='폐기 처리'
            description='정말 폐기 처리하시겠습니까?'
            onConfirm={() => handleSkip(row)}
            okText='확인'
            cancelText='취소'>
            <Button size='small' loading={isLoading}>
              폐기
            </Button>
          </Popconfirm>
        </Space>
      );
    },
    [loadingRowId, handleInbound, handleSkip]
  );

  /**
   * 상품 ID 셀 렌더러 (클릭 시 재고 상세 뷰)
   */
  const ProductIdCellRenderer = useCallback(
    (params: ICellRendererParams<InventoryIssueRow>) => {
      const row = params.data;
      if (!row?.productId) return '-';

      return (
        <Button type='link' size='small' style={{ padding: 0 }} onClick={() => onSetSelectedInventory(row.inventoryId)}>
          {row.productId}
        </Button>
      );
    },
    [onSetSelectedInventory]
  );

  const columnDefs = useMemo<(ColDef<InventoryIssueRow> | ColGroupDef<InventoryIssueRow>)[]>(() => {
    return [
      // Task 정보 그룹
      {
        headerName: 'Task 정보',
        pinned: 'left',
        children: [
          {
            colId: 'taskSummary',
            headerName: '요약',
            width: 180,
            columnGroupShow: 'closed',
            cellRenderer: TaskSummaryCellRenderer,
            pinned: 'left',
            filter: false,
            sortable: false,
          },
          {
            field: 'category',
            headerName: '카테고리',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: CategoryCellRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: taskCategoryValueFormatter,
              convertValuesToStrings: true,
            },
            enableRowGroup: true,
          },
          {
            field: 'taskStatus',
            headerName: 'Task 상태',
            width: 120,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: ClickableTaskStatusCellRenderer,
            filterParams: {
              valueFormatter: taskStatusValueFormatter,
            },
            enableRowGroup: true,
          },
          {
            field: 'taskId',
            headerName: 'Task ID',
            width: 140,
            pinned: 'left',
            columnGroupShow: 'open',
            enableRowGroup: true,
          },
          {
            field: 'issueCaseId',
            headerName: 'Case ID',
            width: 140,
            pinned: 'left',
            columnGroupShow: 'open',
            enableRowGroup: true,
          },
        ],
      },
      {
        headerName: '일시',
        children: [
          {
            field: 'targetDate',
            headerName: '적용 날짜',
            width: 130,
            cellRenderer: TargetDateCellRenderer,
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 120,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
          {
            field: 'finishedAt',
            headerName: '완료일시',
            width: 120,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
        ],
      },

      // 매장
      {
        field: 'storeName',
        headerName: '매장',
        width: 150,
        enableRowGroup: true,
        tooltipField: 'storeId',
      },
      // 상품 정보 그룹
      {
        headerName: '상품 정보',
        openByDefault: true,
        children: [
          {
            field: 'productName',
            headerName: '상품명',
            width: 230,
          },
          {
            field: 'productId',
            headerName: '상품 ID',
            width: 120,
            columnGroupShow: 'open',
            cellRenderer: ProductIdCellRenderer,
          },
          {
            field: 'quantity',
            headerName: '수량',
            width: 80,
            type: 'numericColumn',
          },
          {
            field: 'price',
            headerName: '단가',
            width: 100,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
            type: 'numericColumn',
          },
        ],
      },
      // 재고 처리 정보 그룹
      {
        headerName: '재고 처리',
        openByDefault: true,
        children: [
          {
            field: 'inventoryStatus',
            headerName: '처리 상태',
            width: 120,
            cellRenderer: InventoryStatusCellRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['pending', 'completed', 'skipped', 'failed'],
              valueFormatter: (params: ValueFormatterParams) => {
                const status = params.value as keyof typeof inventoryStatusBadgeMap;
                return inventoryStatusBadgeMap[status]?.text || params.value;
              },
            },
            enableRowGroup: true,
          },
          {
            colId: 'inventoryAction',
            headerName: '액션',
            width: 140,
            cellRenderer: InventoryActionCellRenderer,
            filter: false,
            sortable: false,
          },
          {
            field: 'inventoryIssueId',
            headerName: '재고 이슈 ID',
            width: 140,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) => params.value || '-',
          },
          {
            field: 'processedAt',
            headerName: '처리 시각',
            width: 140,
            columnGroupShow: 'open',
            valueFormatter: (params: ValueFormatterParams) =>
              params.value ? formatDate(new Date(params.value), 'MM.dd HH:mm') : '-',
          },
        ],
      },
    ];
  }, [
    InventoryActionCellRenderer,
    ProductIdCellRenderer,
    CategoryCellRenderer,
    ClickableTaskStatusCellRenderer,
    TargetDateCellRenderer,
    TaskSummaryCellRenderer,
  ]);

  return (
    <>
      <div className='ag-theme-alpine height100'>
        {isLoading && <Loading title='데이터를 불러오고 있습니다.' />}
        <AgGridReact<InventoryIssueRow>
          ref={handleGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.rowId}
          rowHeight={36}
          headerHeight={32}
          groupHeaderHeight={32}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          onGridReady={() => loadGridState(gridRef, ISSUE_INVENTORY_MANAGEMENT_TABLE_ID)}
          groupDisplayType='groupRows'
          suppressRowGroupHidesColumns={true}
        />
      </div>
      {modalData && <IssuePanelModal open={modalOpen} onClose={handleCloseModal} {...modalData} />}
    </>
  );
};

export default IssueInventoryManagementTable;
